// Whether a dose was given early or late, and what state a due dose is in
// (docs/plans/phase2-emar-inpatient-pharmacy.md §4.2). The windows are the facility's
// settings and are empty until someone sets them: TODO(clinical-verify) plan E2. Without a
// window nothing is judged early or late, and no dose is "overdue", only "due".

export const ADMINISTRATION_OUTCOMES = ['given', 'held', 'refused', 'missed'] as const;
export type AdministrationOutcome = (typeof ADMINISTRATION_OUTCOMES)[number];

export const DOSE_TIMINGS = ['on_time', 'early', 'late', 'not_assessed', 'unscheduled'] as const;
export type DoseTiming = (typeof DOSE_TIMINGS)[number];

export const DOSE_STATES = [
  'upcoming',
  'due',
  'overdue',
  'given',
  'held',
  'refused',
  'missed',
] as const;
export type DoseState = (typeof DOSE_STATES)[number];

export interface EmarWindows {
  /** Minutes before the due time a dose may be given without being early; null: not set. */
  readonly earlyMinutes: number | null;
  /** Minutes after the due time a dose may be given without being late; null: not set. */
  readonly lateMinutes: number | null;
}

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

export function doseTiming(
  due: Date,
  at: Date,
  windows: EmarWindows,
): { timing: Exclude<DoseTiming, 'unscheduled'>; minutesFromDue: number } {
  const minutesFromDue = Math.round((at.getTime() - due.getTime()) / MINUTE_MS);
  if (minutesFromDue < 0) {
    if (windows.earlyMinutes === null) return { timing: 'not_assessed', minutesFromDue };
    return { timing: -minutesFromDue > windows.earlyMinutes ? 'early' : 'on_time', minutesFromDue };
  }
  if (minutesFromDue === 0) return { timing: 'on_time', minutesFromDue };
  if (windows.lateMinutes === null) return { timing: 'not_assessed', minutesFromDue };
  return { timing: minutesFromDue > windows.lateMinutes ? 'late' : 'on_time', minutesFromDue };
}

/** A scheduled dose's state now: its recorded outcome, or where it is in time. */
export function doseState(
  due: Date,
  now: Date,
  windows: EmarWindows,
  recorded: AdministrationOutcome | null,
): DoseState {
  if (recorded !== null) return recorded;
  const t = now.getTime();
  const opensAt = due.getTime() - (windows.earlyMinutes ?? 0) * MINUTE_MS;
  if (t < opensAt) return 'upcoming';
  if (windows.lateMinutes !== null && t > due.getTime() + windows.lateMinutes * MINUTE_MS) {
    return 'overdue';
  }
  return 'due';
}

/** Active doses given in the 24 hours before now, for an as-needed order's maximum. */
export function prnGivenInLastDay(
  records: readonly { outcome: AdministrationOutcome; status: string; administeredAt: Date }[],
  now: Date,
): number {
  const since = now.getTime() - DAY_MS;
  return records.filter(
    (r) =>
      r.outcome === 'given' &&
      r.status === 'active' &&
      r.administeredAt.getTime() > since &&
      r.administeredAt.getTime() <= now.getTime(),
  ).length;
}
